// Is a layer's feed delivering, or should its control be locked?
//
// Its own module, importing nothing, for the same reason pin-layout.ts and
// map-style.ts are: live.ts reads import.meta.env at load and cannot be
// imported by a test runner. This rule decides whether a control is usable, so
// it is worth testing directly.

/** The fields of a status line this rule reads. */
export interface FeedStatus {
  configured: boolean;
  updatedAt: number;
  error: string;
}

/**
 * Whether a layer should be greyed out and locked.
 *
 * `configured` does not mean the same thing to every provider, which is how the
 * fuel row stayed selectable while every request came back 403. Vessels binds it
 * to data arriving, so false means down. Fuel binds it to credentials being
 * present — which they are; the block is geographic and happens at CloudFront
 * before the request reaches the API.
 *
 * So the second test asks the honest question: has this layer anything to show,
 * and is it complaining? Never delivered AND an error is a layer with nothing
 * on the map and a reason why.
 *
 * Deliberately neither half alone. On `error` alone, a feed with data and one
 * bad refresh would flicker its control on every transient upstream blip, when
 * what is already drawn is still worth looking at. On `updatedAt` alone, every
 * layer would be locked for the first seconds after a deploy, when the truth is
 * that it has not run yet rather than that it is broken.
 */
export function isFeedDown(status: FeedStatus): boolean {
  if (!status.configured) return true;
  return status.updatedAt === 0 && Boolean(status.error);
}
